import { useRouter } from 'next/router';
import { useState, useCallback, useContext } from 'react';
import H5 from '@base/H5/H5';
import P2 from '@base/P2/P2';
import Tags from '@base/Tags/Tags';
import PlannerCardFrame from '@cont/PlannerV2/PlannerCards/PlannerCardFrame';
import styled from '@emotion/styled';
import MoreHoriz from '@icon/MoreHoriz';
import { Fonts, Spacings } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import PropTypes from 'prop-types';
import { Context as PlannerItemContext } from '@context/PlannerItemContext';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { PLANNER_EVENT_CLICKED, PLANNER_PAGE } from '@constants/segment';
import {
  ScheduleItemTypeLabel,
  ScheduleItemStatusLabel,
  scheduleItemTypes,
  plannerEventTypes
} from '@constants/schedule';
import Tooltip from '@base/Tooltip/Tooltip';
import NImage from '@base/Image';
import CentreDoor from 'aquilla/icons/misc/CentreDoor';
import OfflineClassIcon from '@icon/OfflineClassIcon';
import { classCardHeight, eventPadding } from '../planner-constants';
import EducatorThumbnail from './EducatorThumbnail';

export const CardDescription = styled(H5)`
  padding-right: ${Spacings.SPACING_6B};
  text-decoration: ${({ $isClassDidNotHappen }) =>
    $isClassDidNotHappen ? 'line-through' : 'none'};
  max-width: ${({ $isClassCard }) => ($isClassCard ? '249px' : '100%')};
  line-height: ${Spacings.SPACING_5B};
`;

const MenuButton = styled.div`
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
  position: relative;
  border-radius: ${Spacings.SPACING_6B};
  cursor: pointer;
  justify-self: center;
  transition: background-color 200ms ease-in-out;
  &:hover {
    background-color: var(--color-divider);
  }
`;

const Avatar = styled(NImage)`
  border-radius: 50%;
  object-fit: cover;
`;

export const ContentOuterWrapper = styled.div`
  background-color: var(--color-base-1);
  box-shadow: ${boxShadowGenerator()};
  opacity: ${({ $notAccessible }) => ($notAccessible ? 0.5 : 1)};
  border-radius: ${Spacings.SPACING_2B};
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'auto')};
  &:hover {
    box-shadow: ${({ $isClickable }) =>
      $isClickable
        ? boxShadowGenerator({
            yOffset: Spacings.SPACING_1B,
            blur: Spacings.SPACING_2B
          })
        : boxShadowGenerator()};
  }
`;

const MainContentWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: grid;
  border-radius: ${({ $hasChildren }) =>
    $hasChildren
      ? `${Spacings.SPACING_2B} ${Spacings.SPACING_2B} 0 0`
      : Spacings.SPACING_2B};
  padding: ${eventPadding}px;
  padding-bottom: ${({ $isClassCard }) =>
    $isClassCard ? `${Spacings.SPACING_5B}` : Spacings.SPACING_4B};
  grid-row-gap: ${Spacings.SPACING_1B};
  grid-column-gap: ${Spacings.SPACING_1B};
  grid-template-columns: 1fr 40px;
  grid-template-rows: max-content 1fr;
  transition: background-color 200ms ease-in-out;
  height: ${({ $isClassCard }) =>
    $isClassCard ? `${classCardHeight}px` : 'auto'};
  &:hover {
    background-color: ${({ $isBgChangedOnHover }) =>
      $isBgChangedOnHover ? 'var(--color-divider)' : 'none'};
  }
`;

export const Title = styled(P2)`
  font-weight: ${Fonts.BOLD};
  letter-spacing: 0.02em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  line-height: 100%;
  grid-column: ${({ $showMenu }) => ($showMenu ? '1/2' : '1/3')};
`;

const Info = styled(P2)`
  display: flex;
  align-items: center;
  margin-top: ${Spacings.SPACING_1B};
`;

const LiveTag = styled(Tags)`
  margin-right: 6px;
  height: 16px;
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 12px;
  font-weight: bold;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  color: ${({ $isStreamingDisabled }) =>
    $isStreamingDisabled && 'var(--color-live-tag)'};
  background: ${({ $isStreamingDisabled }) =>
    $isStreamingDisabled &&
    'linear-gradient(0deg, rgba(235, 87, 87, 0.10) 0%, rgba(235, 87, 87, 0.10) 100%), var(--color-base-1)'};
  gap: 4px;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
`;

const AuthorName = styled(P2)`
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ToolTipText = styled.div`
  font-size: ${Fonts.ULTRA_SMALL};
`;

const StyledTooltip = styled(Tooltip)`
  & {
    .tooltip {
      margin: ${Spacings.SPACING_10} ${Spacings.SPACING_0B};
    }
  }
`;

const secondaryTextColor = 'var(--color-text-secondary)';

const PlayIconWrapper = styled.div`
  margin-right: ${Spacings.SPACING_2B};
  display: flex;
  align-items: center;
`;

const VenueWrapper = styled.div`
  margin-top: ${Spacings.SPACING_2B};
  display: flex;
`;

const VenueName = styled(P2)`
  margin-left: ${Spacings.SPACING_2B};
  color: var(--color-text-secondary);
  line-height: ${Spacings.SPACING_4B};
`;

const CentreIconWrapper = styled.div`
  margin-right: ${Spacings.SPACING_1B};
  display: flex;
`;

const BaseCard = ({
  title,
  titleColor,
  description,
  info,
  isLive,
  isClassDidNotHappen,
  isUpcoming,
  hasCompleted,
  author,
  showAuthor,
  showMenu,
  activityAt,
  onItemClick,
  children,
  rightIcon,
  isClickable,
  isCancelled,
  isClassCard = false,
  item,
  showVenue,
  venueName,
  isOffline,
  isStreamingDisabled = false,
  videoUrl
}) => {
  const titleFinalColor =
    isClassDidNotHappen || hasCompleted ? secondaryTextColor : titleColor;
  const descColor = isClassDidNotHappen
    ? secondaryTextColor
    : 'var(--color-text-primary)';

  /**
   * We're having to use allowMenuClick state due to a bug `LEX-2201`. What it exactly does it disable menu click for a few seconds on item click.
   * This allows for item click actions like opening class in the same window with overlapping frame prior to menu being clicked & shown with higher z-index.
   * We arrived at this since class is opened through window.pushState which Next/Route doesn't listen to and subsequently cannot send change events.
   * The only other alternative is to proxy pushState that is known to be not consistent across browsers and is a bit hacky.
   */
  const [allowMenuClick, setAllowMenuClick] = useState(true);
  const [showClassTooltip, setShowClassTooltip] = useState(false);
  const [showMenuTooltip, setShowMenuTooltip] = useState(false);
  const {
    query: { goalUID }
  } = useRouter();

  const goalSegmentData = useGoalEventProps({ goalUID });
  const handleSendSegmentData = () => {
    let eventStatus = ScheduleItemStatusLabel[item.status];
    if (isLive) {
      eventStatus = 'live';
    }
    let clasSpecificData = {};
    if (item.type === scheduleItemTypes.CLASS) {
      const { quizzes, notes } = item.attachments;
      clasSpecificData = {
        is_notes_enabled: notes?.length > 0,
        is_practice_enabled: quizzes?.length > 0
      };
    }
    segment.track(PLANNER_EVENT_CLICKED, {
      ...goalSegmentData,
      ...clasSpecificData,
      last_primary_source: PLANNER_PAGE,
      last_primary_source_section: 'Planner',
      type: ScheduleItemTypeLabel[item.type],
      event_status: eventStatus,
      is_offline_planner: item?.isOffline || false
    });
  };

  const { showActionMenu } = useContext(PlannerItemContext);

  const handleMenuClick = useCallback(
    (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      if (allowMenuClick) {
        showActionMenu(ev.currentTarget, item);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowMenuClick]
  );

  // Please see comments above component for the below setTimeout
  const resetAllowMenuClick = () =>
    setTimeout(() => setAllowMenuClick(true), 2000);

  const handleItemClick = useCallback(
    (ev) => {
      ev.preventDefault();
      if (isClickable && !isClassDidNotHappen) {
        handleSendSegmentData();
        setAllowMenuClick(false);
        onItemClick(ev);
        resetAllowMenuClick();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isClickable, isClassDidNotHappen]
  );
  const onMenuMouseOver = (event) => {
    event.stopPropagation();

    if (item.eventType === plannerEventTypes.CLASS_WITH_PRACTICE) {
      return setShowMenuTooltip(true);
    }
  };

  const onMenuMouseOut = () => {
    setShowMenuTooltip(false);
  };

  const onClassMouseOver = () => {
    if (item.eventType === plannerEventTypes.CLASS_WITH_PRACTICE) {
      return setShowClassTooltip(true);
    }
  };

  const onClassMouseOut = () => {
    setShowClassTooltip(false);
  };

  return (
    <PlannerCardFrame activityAt={activityAt}>
      <ContentOuterWrapper $isClickable={isClickable}>
        <StyledTooltip
          title={<ToolTipText>Watch class</ToolTipText>}
          open={showClassTooltip}
          placement="top"
          maxWidth={250}
        >
          <MainContentWrapper
            $hasAuthor={author}
            $hasRightIcon={!!rightIcon}
            $hasChildren={!!children}
            $isClassCard={isClassCard}
            onClick={handleItemClick}
            $isUpcoming={isUpcoming}
            $isBgChangedOnHover={showClassTooltip}
            onMouseOver={onClassMouseOver}
            onMouseOut={onClassMouseOut}
          >
            <Title
              data-testid="planner-card-title"
              lineClamp={1}
              color={titleFinalColor}
              $showMenu={showMenu}
            >
              {title}
            </Title>
            {showMenu && (
              <StyledTooltip
                title={<ToolTipText>More options</ToolTipText>}
                open={showMenuTooltip}
                placement="top"
                maxWidth={250}
                onMouseOver={onMenuMouseOver}
                onMouseOut={onMenuMouseOut}
              >
                <MenuButton
                  data-testid="planner-card-btn-menu"
                  onClick={handleMenuClick}
                  className="menuButton"
                  onMouseOver={onMenuMouseOver}
                  onMouseOut={onMenuMouseOut}
                >
                  <MoreHoriz size="24px" color="var(--color-text-secondary)" />
                </MenuButton>
              </StyledTooltip>
            )}
            <InfoWrapper>
              <div>
                <CardDescription
                  data-testid="planner-card-description"
                  lineClamp={isClassCard ? 2 : 1}
                  color={descColor}
                  $isClassDidNotHappen={isClassDidNotHappen}
                  $isClassCard={isClassCard}
                  title={description}
                >
                  {description}
                </CardDescription>
                <Info
                  color={secondaryTextColor}
                  data-testid="planner-card-info"
                >
                  {isLive && !isClassDidNotHappen && (
                    <LiveTag
                      data-testid="planner-class-live-tag"
                      type="live"
                      $isStreamingDisabled={isStreamingDisabled}
                    >
                      {isStreamingDisabled ? (
                        <>
                          <OfflineClassIcon height={16} width={16} /> Live at
                          Centre
                        </>
                      ) : (
                        'Live'
                      )}
                    </LiveTag>
                  )}
                  {isOffline && (isLive || isUpcoming) && (
                    <CentreIconWrapper>
                      <CentreDoor />
                    </CentreIconWrapper>
                  )}
                  {info}
                </Info>
              </div>
              {showAuthor && author && (
                <AuthorInfo>
                  {isClassCard ? (
                    !!rightIcon &&
                    (!isStreamingDisabled || videoUrl) && (
                      <PlayIconWrapper>{rightIcon}</PlayIconWrapper>
                    )
                  ) : (
                    <Avatar
                      src={author.thumbnail}
                      width={Spacings.SPACING_5B}
                      height={Spacings.SPACING_5B}
                      alt={author.name}
                    />
                  )}
                  <AuthorName data-testid="planner-author-name">
                    {author.name}
                  </AuthorName>
                </AuthorInfo>
              )}
            </InfoWrapper>
            {isClassCard ? (
              <EducatorThumbnail
                fallback={author?.thumbnail}
                isCancelled={isClassDidNotHappen || isCancelled}
                color={
                  isClassDidNotHappen || isCancelled
                    ? 'var(--color-i-gray-25)'
                    : titleColor
                }
                newThumbnail={author.avatarV1}
                oldThumbnail={author.oldAvatar}
              />
            ) : (
              rightIcon
            )}
            {showVenue ? (
              <VenueWrapper>
                <NImage
                  alt="venue-location"
                  src="/planner/venue-location.svg"
                  height={Spacings.SPACING_3B}
                  width={Spacings.SPACING_10}
                />
                <VenueName>{venueName}</VenueName>
              </VenueWrapper>
            ) : null}
          </MainContentWrapper>
        </StyledTooltip>
        {children}
      </ContentOuterWrapper>
    </PlannerCardFrame>
  );
};

BaseCard.propTypes = {
  title: PropTypes.string.isRequired,
  titleColor: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  info: PropTypes.node.isRequired,
  isLive: PropTypes.bool.isRequired,
  isClassDidNotHappen: PropTypes.bool,
  isUpcoming: PropTypes.bool.isRequired,
  isClickable: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  author: PropTypes.objectOf(PropTypes.any),
  showAuthor: PropTypes.bool,
  showMenu: PropTypes.bool,
  children: PropTypes.node,
  onItemClick: PropTypes.func,
  rightIcon: PropTypes.node
};

BaseCard.defaultProps = {
  children: null,
  author: {},
  showAuthor: false,
  showMenu: false,
  rightIcon: null,
  onItemClick: null,
  isClassDidNotHappen: false
};

export default BaseCard;
